import React from 'react';
import { ModelSummary as ModelSummaryType } from '../types';
import { formatTime, formatConfidence } from '../utils/inferenceUtils';

interface ModelSummaryProps {
  summaries: ModelSummaryType[];
}

const ModelSummary: React.FC<ModelSummaryProps> = ({ summaries }) => {
  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'text-green-600';
    if (rating >= 3) return 'text-yellow-600';
    if (rating >= 2) return 'text-orange-600';
    return 'text-red-600';
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Best': return 'bg-green-100 text-green-800';
      case 'Good': return 'bg-blue-100 text-blue-800';
      case 'Fair': return 'bg-yellow-100 text-yellow-800';
      case 'Poor': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-medium text-gray-900">Model Performance Summary</h2>
        <p className="text-sm text-gray-600 mt-1">
          Overall performance metrics across all questions
        </p>
      </div>

      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {summaries.map((summary) => (
            <div
              key={summary.modelName}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-medium text-gray-900">{summary.modelName}</h3>
                <div className={`text-lg font-bold ${getRatingColor(summary.averageRating)}`}>
                  {summary.averageRating.toFixed(1)}
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Questions Scored:</span>
                  <span className="font-medium">{summary.totalQuestions}</span>
                </div>

                {summary.inferenceStats && (
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="text-xs font-medium text-gray-700 mb-1">Inference Time:</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Avg:</span>
                        <span className="font-medium">{formatTime(summary.inferenceStats.average)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Min / Max:</span>
                        <span className="font-medium">{formatTime(summary.inferenceStats.min)} / {formatTime(summary.inferenceStats.max)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">P95:</span>
                        <span className="font-medium">{formatTime(summary.inferenceStats.p95)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {summary.confidenceStats && (
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="text-xs font-medium text-gray-700 mb-1">Confidence Scores:</div>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Avg:</span>
                        <span className="font-medium">{formatConfidence(summary.confidenceStats.average)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Min / Max:</span>
                        <span className="font-medium">{formatConfidence(summary.confidenceStats.min)} / {formatConfidence(summary.confidenceStats.max)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">Median:</span>
                        <span className="font-medium">{formatConfidence(summary.confidenceStats.median)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-600">P95:</span>
                        <span className="font-medium">{formatConfidence(summary.confidenceStats.p95)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-1 border-t border-gray-200 pt-2 mt-2">
                  <div className="text-xs font-medium text-gray-700 mb-1">Category Distribution:</div>
                  {Object.entries(summary.categoryCounts).map(([category, count]) => (
                    <div key={category} className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">{category}:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-xs font-medium">{count}</span>
                        <div className={`w-2 h-2 rounded-full ${getCategoryColor(category).replace('text-', 'bg-').replace('100', '400')}`} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Progress Bar */}
                <div className="mt-3">
                  <div className="flex space-x-1">
                    {Object.entries(summary.categoryCounts).map(([category, count]) => {
                      const percentage = summary.totalQuestions > 0 
                        ? (count / summary.totalQuestions) * 100 
                        : 0;
                      
                      return (
                        <div
                          key={category}
                          className={`h-2 rounded-full ${getCategoryColor(category).replace('text-', 'bg-').replace('100', '400')}`}
                          style={{ width: `${percentage}%` }}
                          title={`${category}: ${count} (${percentage.toFixed(1)}%)`}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {summaries.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">No scores available yet. Start scoring questions to see summaries.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelSummary;
